import { QueryClient, MutationCache, QueryCache } from '@tanstack/react-query';
import { ApiError } from './api.js';
import { reportSaveError, reportReadError } from './saveErrors.js';

export const queryClient = new QueryClient({
  // ── No save fails in silence ──────────────────────────────────────────────
  //
  // Every mutation error passes through here. If the mutation declares its own
  // onError, this stays quiet and lets the screen present the failure in
  // context — next to the field, which is always better. If it does NOT, this
  // is the only thing standing between a refused save and a screen that just
  // sits there.
  //
  // A sweep on 2026-08-27 found 75 of 230 mutations with no error handling at
  // all, after three of them cost a morning. The default is now the safe one,
  // and a mutation added next month is covered without anybody remembering to
  // cover it.
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => {
      if (mutation.options.onError) return;
      reportSaveError(error);
    },
  }),
  // ── ולא רק שמירה — גם קריאה ────────────────────────────────────────────
  //
  // The mutation half of this was built after three silent saves cost a
  // morning. The READ half was left, and it is the larger surface: a query
  // that fails renders `q.data ?? []`, and an empty table is indistinguishable
  // from a table with nothing in it. A 403 on a picker looks like a picker
  // with no options; a 500 on a work queue looks like a quiet day.
  //
  // Same rule as the mutation cache: silent when the screen handles it —
  // whether through its own `onError` or through the `meta.handled` flag a
  // screen sets when it renders `isError` itself — and otherwise the last
  // thing standing between a failed read and a screen that lies.
  //
  // Background refetches of data already on screen stay quiet: the person is
  // looking at real rows, and a banner about a refresh that will retry on its
  // own is noise. Only a query with NO data to show speaks up.
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (query.options.meta?.handled) return;
      if (query.state.data !== undefined) return;
      reportReadError(error);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: true,
      retry: (failureCount, error) => {
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
        return failureCount < 2;
      },
    },
    mutations: { retry: false },
  },
});
